import axios from 'axios'
import { reportError } from './errorReporting'

export const NETWORK_ERROR_MESSAGE =
  'Sem conexão com o servidor. Verifique sua internet e tente novamente.'

export const GENERIC_ERROR_MESSAGE =
  'Não foi possível concluir a operação. Tente novamente.'

/**
 * Requisição abortada não é falha: é a tela dizendo que não precisa mais da resposta.
 *
 * Precisa vir antes de `isNetworkError` porque um cancelamento também chega sem
 * `response` — sem esta checagem, sair de uma página antes de a resposta voltar
 * mostraria "sem conexão com o servidor" e mandaria um alerta falso para o Sentry.
 */
export function isCanceled (error) {
  return axios.isCancel (error) || error?.code === 'ERR_CANCELED'
}

export function isNetworkError (error) {
  return !isCanceled (error) && !error?.response
}

export function getApiErrorStatus (error) {
  return error?.response?.status ?? null
}

/**
 * Mensagem segura para exibir ao usuário.
 * Nunca lança: quando a requisição falha sem resposta (offline, timeout, DNS),
 * error.response é undefined e o acesso direto a error.response.data quebraria
 * dentro do próprio catch, deixando a tela presa em loading.
 */
export function getApiErrorMessage (error, fallback = GENERIC_ERROR_MESSAGE) {
  if (isNetworkError (error)) return NETWORK_ERROR_MESSAGE

  return error.response.data?.message ?? fallback
}

/**
 * Log de erro sem dados sensíveis.
 * Não registra error.config.headers (contém o Bearer token) nem
 * error.config.data (contém senha no login). O build do Vite não remove
 * console.* da produção, então o corte é feito aqui: em produção
 * qualquer um abre o DevTools e lê o que for registrado.
 */
export function logApiError (error, context = '') {
  // Cancelamento é fluxo normal de navegação, não incidente.
  if (isCanceled (error)) return

  const status = getApiErrorStatus (error)

  /**
   * Falha de rede, erro do servidor e 403 viram evento. Os demais 4xx são respostas
   * esperadas — validação, não encontrado — e inundariam o painel a ponto de esconder o
   * que importa.
   *
   * O 403 é a exceção entre os 4xx: significa que o cliente acha que tem uma permissão
   * que o servidor não concede. A tela mostra só a mensagem genérica, então sem
   * telemetria essa divergência não aparece para ninguém — era o "log silencioso" do
   * levantamento de débito técnico.
   */
  if (status === null || status === 403 || status >= 500) {
    reportError (error, { context, status })
  }

  if (import.meta.env.PROD) return

  console.log ('[api]', context, {
    status,
    method: error?.config?.method ?? null,
    url: error?.config?.url ?? null,
    message: error?.response?.data?.message ?? error?.message ?? null
  })
}
